import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import JSMpeg from '@cycjimmy/jsmpeg-player';

export interface IceServerConfiguration {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export interface WebRtcSessionCredentials {
  signalingUrl: string;
  iceServers: IceServerConfiguration[];
  username: string;
  password: string;
}

export type WebRtcControlEvent =
  | { type: 'mouse'; action: 'move' | 'down' | 'up'; button?: number; x: number; y: number; clientWidth: number; clientHeight: number }
  | { type: 'wheel'; deltaX: number; deltaY: number; x: number; y: number; clientWidth: number; clientHeight: number }
  | { type: 'keyboard'; action: 'down' | 'up'; key: string; code: string; ctrlKey: boolean; altKey: boolean; shiftKey: boolean; metaKey: boolean };

type OfferPayload = {
  sessionId: string;
};

type SessionTokenResponse = { token: string };

@Injectable({
  providedIn: 'root'
})
export class WebRtcService {
  private _player?: any;
  private _activeSessionId?: string;
  private _currentSession?: { sessionId: string; credentials: WebRtcSessionCredentials };

  constructor(private readonly http: HttpClient) {}

  setSessionCredentials(sessionId: string, credentials: WebRtcSessionCredentials): void {
    this._currentSession = { sessionId, credentials };
  }

  getSessionCredentials(sessionId: string): WebRtcSessionCredentials {
    if (!this._currentSession || this._currentSession.sessionId !== sessionId) {
      throw new Error('Missing WebRTC session credentials.');
    }
    return this._currentSession.credentials;
  }

  async connect(
    sessionId: string,
    credentials: WebRtcSessionCredentials,
    resolution: { width: number; height: number },
    canvas: HTMLCanvasElement
  ): Promise<void> {
    this.disconnect();
    this._activeSessionId = sessionId;
    const tokenResponse = await lastValueFrom(this.http.post<SessionTokenResponse>(
      `${credentials.signalingUrl}/session-token`,
      {
        sessionId
      } satisfies OfferPayload,
      { headers: this.buildAuthHeaders(credentials) }
    ));
    if (!tokenResponse?.token) {
      throw new Error('Unable to create authenticated video stream token.');
    }
    const wsBase = this.toWebSocketBaseUrl(credentials.signalingUrl);
    const streamUrl = `${wsBase}/video?token=${encodeURIComponent(tokenResponse.token)}&width=${resolution.width}&height=${resolution.height}`;
    const player = this.createJSMpegPlayer(streamUrl, canvas);
    this._player = player;
  }

  sendInputEvent(event: WebRtcControlEvent): void {
    this.sendInputFallback(event);
  }

  disconnect(): void {
    this._activeSessionId = undefined;
    this._player?.destroy?.();
    this._player?.stop?.();
    this._player?.source?.destroy?.();
    this._player?.source?.socket?.close?.();
    this._player = undefined;
  }

  private sendInputFallback(event: WebRtcControlEvent): void {
    if (!this._activeSessionId || !this._currentSession || this._currentSession.sessionId !== this._activeSessionId) {
      return;
    }
    const endpoint = `${this._currentSession.credentials.signalingUrl}/input`;
    void lastValueFrom(this.http.post(endpoint, {
      sessionId: this._activeSessionId,
      event
    }, {
      headers: this.buildAuthHeaders(this._currentSession.credentials)
    })).catch(() => undefined);
  }

  private buildAuthHeaders(credentials: WebRtcSessionCredentials): Record<string, string> {
    const encoded = btoa(`${credentials.username}:${credentials.password}`);
    return {
      Authorization: `Basic ${encoded}`
    };
  }

  private toWebSocketBaseUrl(signalingUrl: string): string {
    const url = new URL(signalingUrl);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return url.toString().replace(/\/$/, '');
  }

  private resolveJSMpegPlayerCtor(): new (...args: any[]) => any {
    const mod = JSMpeg as any;
    const ctor = mod?.Player ?? mod?.default?.Player ?? mod?.default ?? mod;
    if (typeof ctor === 'function') {
      return ctor;
    }
    throw new Error('JSMpeg Player constructor not found.');
  }

  private createJSMpegPlayer(streamUrl: string, canvas: HTMLCanvasElement): any {
    const ctor = this.resolveJSMpegPlayerCtor();
    return new ctor(streamUrl, {
      canvas,
      autoplay: true,
      audio: false,
      video: true,
      loop: false,
      disableGl: true,
      preserveDrawingBuffer: true
    });
  }
}
